import type { RequestContext } from "../context/request-context.js";
import type { DocumentHoldRepository } from "../../infrastructure/database/memory/in-memory-document-hold-repository.js";
import {
  HOLD_COMMENT_REQUIRED,
  HOLD_DOCUMENT_TYPE_LABELS,
  normalizeDocumentHold,
  validateHoldReason,
  type DocumentHold,
  type HoldActor,
  type HoldCategory,
  type HoldDocumentSummary,
  type HoldDocumentType
} from "../../domain/review/document-hold.js";
import { AppError, ErrorCode } from "../../shared/errors/app-error.js";
import { RbacAuthorizationService } from "../../permissions/authorization.js";
import { PermissionCode } from "../../permissions/permissions.js";
import type { AuditRecorder } from "../../audit/audit-recorder.js";
import { AuditAction } from "../../audit/audit-log.js";
import { notificationService } from "./notification-service.js";

const randomUUID = () => globalThis.crypto.randomUUID();

/**
 * O status do módulo NÃO é reescrito: o HOLD é um estado sobreposto, bloqueia
 * edição pelos ganchos e, ao liberar, o documento volta exatamente ao status
 * anterior — que confere com o gravado no HOLD.
 */
export interface HoldState {
  hold: DocumentHold | null;
  onHold: boolean;
  reason: string;
}

/** Ganchos para os módulos consultarem o bloqueio antes de salvar/validar/assinar. */
export interface DocumentHoldHooks {
  state(documentType: HoldDocumentType, documentId: string): Promise<HoldState>;
}

export interface PutOnHoldInput {
  documentType: HoldDocumentType;
  documentId: string;
  summary: HoldDocumentSummary;
  currentStatus: { status: string; label: string };
  reason: string;
  category?: HoldCategory | null;
}

export interface HoldQueueFilters {
  documentType?: HoldDocumentType | "ALL";
  clientId?: string;
  status?: "EM_HOLD" | "LIBERADO" | "ALL";
  search?: string;
}

export class DocumentHoldService {
  constructor(
    private readonly repo: DocumentHoldRepository,
    private readonly identityRepo?: any,
    private readonly audit?: AuditRecorder
  ) {}

  private get auth() { return new RbacAuthorizationService(); }

  hooks(): DocumentHoldHooks {
    return { state: (documentType, documentId) => this.state({} as RequestContext, documentType, documentId) };
  }

  async state(context: RequestContext, documentType: HoldDocumentType, documentId: string): Promise<HoldState> {
    const hold = await this.repo.findActiveByDocument(context, documentType, documentId);
    if (!hold) return { hold: null, onHold: false, reason: "" };
    return { hold: normalizeDocumentHold(hold), onHold: true, reason: `Documento EM HOLD desde ${new Date(hold.placedAt).toLocaleString("pt-BR")} (${hold.placedBy.name}). Motivo: ${hold.reason}. Use "TIRAR DE HOLD" para continuar.` };
  }

  async getHold(context: RequestContext, id: string): Promise<DocumentHold | null> {
    const hold = await this.repo.findById(context, id);
    return hold ? normalizeDocumentHold(hold) : null;
  }

  async activeHold(context: RequestContext, documentType: HoldDocumentType, documentId: string): Promise<DocumentHold | null> {
    const hold = await this.repo.findActiveByDocument(context, documentType, documentId);
    return hold ? normalizeDocumentHold(hold) : null;
  }

  async historyOf(context: RequestContext, documentType: HoldDocumentType, documentId: string): Promise<readonly DocumentHold[]> {
    return (await this.repo.listByDocument(context, documentType, documentId)).map(normalizeDocumentHold);
  }

  /** ENGENHARIA → RELATÓRIOS EM HOLD (ativos por padrão). */
  async listHolds(context: RequestContext, filters: HoldQueueFilters = {}): Promise<DocumentHold[]> {
    this.auth.require(context, PermissionCode.EngineeringHoldView);
    const q = (filters.search || "").trim().toLowerCase();
    return (await this.repo.listAll(context))
      .map(normalizeDocumentHold)
      .filter(h => !context.activeClientId || h.summary.clientId === String(context.activeClientId))
      .filter(h => (filters.status ?? "EM_HOLD") === "ALL" || h.status === (filters.status ?? "EM_HOLD"))
      .filter(h => !filters.documentType || filters.documentType === "ALL" || h.documentType === filters.documentType)
      .filter(h => !filters.clientId || h.summary.clientId === filters.clientId)
      .filter(h => !q || [h.summary.documentNumber, h.summary.tag, h.reason, h.summary.inspectorName].some(v => (v || "").toLowerCase().includes(q)))
      .sort((a, b) => a.placedAt.localeCompare(b.placedAt));
  }

  /** DEIXAR EM HOLD — motivo obrigatório; registra motivo, usuário, data/hora e o status anterior. */
  async putOnHold(context: RequestContext, input: PutOnHoldInput): Promise<DocumentHold> {
    this.auth.require(context, PermissionCode.ReportPutOnHold);
    const errors = validateHoldReason(input.reason);
    if (errors.length > 0) throw new AppError(ErrorCode.Validation, "Hold reason required", { safeMessage: errors.join(" ") });
    const active = await this.repo.findActiveByDocument(context, input.documentType, input.documentId);
    if (active) throw new AppError(ErrorCode.Conflict, "Already on hold", { safeMessage: "O documento já está EM HOLD." });
    const now = new Date().toISOString();
    const actor = await this.actorOf(context);
    const hold: DocumentHold = {
      id: `hold-${randomUUID()}`,
      documentType: input.documentType,
      documentId: input.documentId,
      summary: input.summary,
      reason: input.reason.trim(),
      category: input.category ?? null,
      previousStatus: input.currentStatus.status,
      previousStatusLabel: input.currentStatus.label,
      placedBy: actor,
      placedAt: now,
      status: "EM_HOLD",
      engineeringComments: [],
      releasedBy: null,
      releasedAt: null,
      releaseNote: "",
      restoredStatus: null,
      history: [{ at: now, by: actor, action: "HOLD_INICIADO", detail: `Motivo: ${input.reason.trim()}${input.category ? ` · Categoria: ${input.category}` : ""} · Status anterior: ${input.currentStatus.label}` }],
      createdAt: now,
      updatedAt: now
    };
    await this.repo.save(context, hold);
    await this.recordAudit(context, AuditAction.DocumentPutOnHold, hold, { reason: hold.reason, category: hold.category, previousStatus: hold.previousStatus });
    return hold;
  }

  /** ANALISAR HOLD → COMENTÁRIO DA ENGENHARIA; o Inspetor responsável é notificado com deep link para o documento. */
  async comment(context: RequestContext, holdId: string, comment: string): Promise<DocumentHold> {
    this.auth.require(context, PermissionCode.EngineeringHoldComment);
    const hold = await this.requireHold(context, holdId);
    if (hold.status !== "EM_HOLD") throw new AppError(ErrorCode.Conflict, "Hold released", { safeMessage: "Este HOLD já foi liberado." });
    const text = (comment || "").trim();
    if (!text) throw new AppError(ErrorCode.Validation, "Comment required", { safeMessage: HOLD_COMMENT_REQUIRED });
    const now = new Date().toISOString();
    const actor = await this.actorOf(context);
    const entry = { id: `hc-${randomUUID()}`, at: now, by: actor, comment: text };
    const updated: DocumentHold = { ...hold, engineeringComments: [...hold.engineeringComments, entry], history: [...hold.history, { at: now, by: actor, action: "COMENTÁRIO_ENGENHARIA", detail: text }], updatedAt: now };
    await this.repo.save(context, updated);
    await this.recordAudit(context, AuditAction.HoldEngineeringCommented, updated, { comment: text });
    const recipient = await this.userIdOfProfessional(hold.summary.inspectorProfessionalId, hold.placedBy.userId);
    if (recipient) {
      try {
        await notificationService.createNotification({
          userId: recipient, type: "INFO", event: "HOLD_ENGINEERING_COMMENTED",
          title: "Engenharia comentou o HOLD",
          message: `Engenharia comentou o HOLD do relatório ${hold.summary.documentNumber}.`,
          entityType: "Hold", entityId: hold.id, clientId: hold.summary.clientId, route: this.holdRoute(hold),
          target: { documentType: hold.documentType, documentId: hold.documentId, revision: hold.summary.revision, sectionId: "hold", itemId: hold.id, fieldId: null, anchor: this.holdRoute(hold), recipientProfessionalId: hold.summary.inspectorProfessionalId },
          idempotencyKey: `HOLD_COMMENT_${entry.id}`
        });
      } catch (e) { console.warn("[HOLD] Falha ao notificar o Inspetor", e); }
    }
    return updated;
  }

  /**
   * TIRAR DE HOLD — restaura o status de workflow anterior: o módulo nunca
   * teve o status reescrito, então `restoredStatus` é o status atual do
   * módulo, que deve conferir com `previousStatus`. Nunca vira "aprovado".
   */
  async release(context: RequestContext, holdId: string, note = "", currentStatus?: { status: string; label: string }): Promise<DocumentHold> {
    this.auth.require(context, PermissionCode.ReportRemoveFromHold);
    const hold = await this.requireHold(context, holdId);
    if (hold.status !== "EM_HOLD") return hold;
    const now = new Date().toISOString();
    const actor = await this.actorOf(context);
    const restored = currentStatus?.status ?? hold.previousStatus;
    const updated: DocumentHold = {
      ...hold, status: "LIBERADO", releasedBy: actor, releasedAt: now, releaseNote: (note || "").trim(), restoredStatus: restored,
      history: [...hold.history, { at: now, by: actor, action: "HOLD_LIBERADO", detail: `${(note || "").trim() || "Sem observação."} · Status restaurado: ${currentStatus?.label ?? hold.previousStatusLabel}` }],
      updatedAt: now
    };
    await this.repo.save(context, updated);
    await this.recordAudit(context, AuditAction.DocumentRemovedFromHold, updated, { note: updated.releaseNote, previousStatus: hold.previousStatus, restoredStatus: restored });
    // A Engenharia que comentou fica sabendo da liberação.
    const notified = new Set<string>();
    for (const c of hold.engineeringComments) {
      if (!c.by.userId || c.by.userId === "system" || notified.has(c.by.userId)) continue;
      notified.add(c.by.userId);
      try {
        await notificationService.createNotification({ userId: c.by.userId, type: "INFO", event: "HOLD_RELEASED", title: "Documento liberado do HOLD", message: `${hold.summary.documentNumber} saiu de HOLD (${actor.name}). Status restaurado: ${currentStatus?.label ?? hold.previousStatusLabel}.`, entityType: "Hold", entityId: hold.id, clientId: hold.summary.clientId, route: hold.summary.route, idempotencyKey: `HOLD_RELEASED_${hold.id}_${c.by.userId}` });
      } catch (e) { console.warn("[HOLD] Falha ao notificar", e); }
    }
    return updated;
  }

  /** Rota que abre o documento/Checklist em HOLD já mostrando motivo e comentários. */
  holdRoute(hold: DocumentHold): string {
    const [path, query] = hold.summary.route.split("?");
    const params = new URLSearchParams(query || "");
    params.set("section", "hold");
    params.set("item", hold.id);
    return `${path}?${params.toString()}`;
  }

  typeLabel(type: HoldDocumentType): string {
    return HOLD_DOCUMENT_TYPE_LABELS[type] || type;
  }

  // ---------------------------------------------------------- HELPERS

  private async requireHold(context: RequestContext, id: string): Promise<DocumentHold> {
    const hold = await this.repo.findById(context, id);
    if (!hold) throw new AppError(ErrorCode.NotFound, "Hold not found", { safeMessage: "HOLD não encontrado." });
    return normalizeDocumentHold(hold);
  }

  private async userIdOfProfessional(professionalId: string | null, fallbackUserId: string | null): Promise<string | null> {
    if (professionalId && this.identityRepo) {
      const professional = this.identityRepo.getProfessional ? await this.identityRepo.getProfessional(professionalId).catch(() => null) : null;
      if (professional?.userId) return String(professional.userId);
      if (this.identityRepo.listUsers) {
        const users: any[] = await this.identityRepo.listUsers().catch(() => []);
        const user = users.find(u => String(u.professionalId || "") === String(professionalId));
        if (user) return String(user.id);
      }
    }
    return fallbackUserId && fallbackUserId !== "system" ? fallbackUserId : null;
  }

  private async actorOf(context: RequestContext): Promise<HoldActor> {
    const userId = context.userId || "system";
    const user = this.identityRepo && context.userId ? await this.identityRepo.getUser(context.userId).catch(() => null) : null;
    const professional = this.identityRepo && user?.professionalId ? await this.identityRepo.getProfessional(user.professionalId).catch(() => null) : null;
    return { userId, professionalId: professional ? String(professional.id) : (context.professionalId ? String(context.professionalId) : null), name: professional?.name || user?.name || userId };
  }

  private async recordAudit(context: RequestContext, action: AuditAction, hold: DocumentHold, metadata: Record<string, unknown>): Promise<void> {
    if (!this.audit) return;
    try {
      await this.audit.record(context, {
        action,
        entityType: `HOLD:${hold.documentType}`,
        entityId: hold.documentId,
        previousState: { status: hold.previousStatus } as any,
        newState: { hold: hold.status } as any,
        metadata: { holdId: hold.id, documentType: hold.documentType, documentId: hold.documentId, documentNumber: hold.summary.documentNumber, performedBy: context.userId || "system", professionalId: context.professionalId || null, at: hold.updatedAt, ...metadata } as any
      });
    } catch (e) {
      console.warn("[HOLD] Falha ao registrar auditoria", e);
    }
  }
}
